import { useEffect, useState, ReactNode } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

import { Lozenge, LozengeProps } from "../../lozenge/src/lozenge";

export interface RadioButtonProps {
    id: string;
    label: string | ReactNode;
    lozenge?: LozengeProps;
    disabled?: boolean;
}

interface InternalRadioButtonProps extends RadioButtonProps {
    checked?: boolean;
    size?: "medium" | "large";
    handleRadioChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const RadioButton = ({
    id,
    label,
    lozenge,
    disabled = false,
    checked = false,
    size,
    handleRadioChange
}: InternalRadioButtonProps) => {
    const [isChecked, setIsChecked] = useState(checked);

    useEffect(() => {
        setIsChecked(checked);
    }, [checked]);

    return (
        <div css={radioContainerStyles}>
            <label
                className={"body-md"}
                css={[radioLabelStyles, disabled && disabledRadioLabelStyles]}
                htmlFor={id}
            >
                <input
                    css={[
                        radioInputStyles,
                        size === "medium" && mediumSizeRadioInputStyles,
                        size === "large" && largeSizeRadioInputStyles,
                        disabled && disabledRadioButtonStyles
                    ]}
                    type="radio"
                    id={id}
                    data-testid={`radio-${id}`}
                    checked={isChecked}
                    disabled={disabled}
                    onChange={(e) => {
                        if (disabled) return;
                        handleRadioChange(e);
                        setIsChecked(!isChecked);
                    }}
                />
                {label}
            </label>
            {lozenge && (
                <Lozenge appearance={lozenge.appearance} text={lozenge.text} />
            )}
        </div>
    );
};

export interface RadioButtonsProps {
    groupLabel: string | ReactNode;
    buttons: RadioButtonProps[];
    selectedButtonId?: string;
    dataProductKey?: string;
    size?: "medium" | "large";
    onChange?: (selectedId: string) => string | void;
    wrap?: boolean;
}

export const RadioButtons = ({
    groupLabel,
    buttons,
    selectedButtonId,
    // @ts-ignore
    dataProductKey,
    size = "medium",
    onChange,
    wrap = true
}: RadioButtonsProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const [currentSelectedButtonId, setSelectedButtonId] = useState<
        string | undefined
    >(selectedButtonId);

    const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        createAnalyticsEvent({
            eventType: "ui",
            // eventName: TODO: event name,
            eventComponent: "radioButton",
            event: "clicked",
            // actionSubject: TODO: action subject,
            action: "clicked"
            // attributes: TODO: attributes
        }).fire();

        setSelectedButtonId(e.target.id);
        if (onChange) {
            onChange(e.target.id);
        }
    };

    return (
        <fieldset css={[containerStyles, wrap && css({ flexWrap: "wrap" })]}>
            <legend className={"body-md"} css={groupLabelStyles}>
                {groupLabel}
            </legend>
            {buttons.map((button) => (
                <RadioButton
                    key={button.id}
                    id={button.id}
                    label={button.label}
                    lozenge={button.lozenge}
                    disabled={button.disabled}
                    checked={currentSelectedButtonId === button.id}
                    size={size}
                    handleRadioChange={handleRadioChange}
                />
            ))}
        </fieldset>
    );
};

const containerStyles = css({
    display: "flex",
    flexDirection: "row",
    border: "none",
    width: "100%",
    gap: `${token("space.400")}`
});

const groupLabelStyles = css({
    float: "left",
    whiteSpace: "nowrap",
    fontWeight: "600"
});

const radioContainerStyles = css({
    display: "inline-flex",
    flexWrap: "nowrap",
    alignItems: "center",
    gap: `${token("space.100")}`,
    flexShrink: 0
});

const radioLabelStyles = css({
    cursor: "pointer",
    gap: `${token("space.100")}`,
    display: "flex",
    alignItems: "center"
});

const mediumSizeRadioInputStyles = css({
    "width": "20px",
    "height": "20px",
    ":checked": {
        "::before": {
            width: "10px",
            height: "10px"
        }
    }
});

const largeSizeRadioInputStyles = css({
    "width": "28px",
    "height": "28px",
    ":checked": {
        "::before": {
            width: "16px",
            height: "16px"
        }
    }
});

const radioInputStyles = css({
    "color": "currentColor",
    "appearance": "none",
    "margin": 0,
    "border": `${token("space.025")} solid ${wacToken("color.primary.black")}`,
    "borderRadius": "50%",
    "cursor": "pointer",
    "display": "grid",
    "placeContent": "center",
    ":focus-visible": {
        outlineOffset: `${token("space.025")}`,
        outline: `${token("space.025")} solid ${wacToken("color.primary.blue")}`
    },
    ":active, :hover": {
        borderColor: wacToken("color.primary.blue")
    },
    ":checked": {
        "borderColor": wacToken("color.primary.blue"),
        "::before": {
            content: '""',
            borderRadius: "50%",
            display: "block",
            backgroundColor: wacToken("color.primary.blue")
        }
    }
});

const disabledRadioButtonStyles = css({
    "cursor": "not-allowed",
    "color": wacToken("color.neutral.light600"),
    "borderColor": wacToken("color.neutral.light600"),
    ":hover, :active": {
        borderColor: wacToken("color.neutral.light600")
    }
});

const disabledRadioLabelStyles = css({
    cursor: "not-allowed",
    color: wacToken("color.neutral.light600")
});
